import { useCallback, useEffect, useState } from "react";

/**
 * useUrlState — syncs a piece of state to a URL query param.
 * Survives reload. Shareable. Works without react-router (uses window.history directly).
 *
 * Usage:
 *   const [q, setQ] = useUrlState("q", "");
 *   const [stage, setStage] = useUrlState("stage", "all");
 *
 * Strings only. For arrays encode as comma-separated. For objects, caller should
 * serialize before passing in.
 */
export default function useUrlState(key, defaultValue = "") {
  const read = () => {
    if (typeof window === "undefined") return defaultValue;
    const params = new URLSearchParams(window.location.search);
    const v = params.get(key);
    return v == null ? defaultValue : v;
  };

  const [value, setValue] = useState(read);

  // Re-read on back/forward
  useEffect(() => {
    const onPop = () => setValue(read());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const update = useCallback((next) => {
    setValue((prev) => {
      const resolved = typeof next === "function" ? next(prev) : next;
      if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        if (resolved == null || resolved === "" || resolved === defaultValue) {
          params.delete(key);
        } else {
          params.set(key, String(resolved));
        }
        const qs = params.toString();
        const url = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
        window.history.replaceState(null, "", url);
      }
      return resolved;
    });
  }, [key, defaultValue]);

  return [value, update];
}
